import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TEMPLATES_BY_ID,
  TEMPLATE_COLLECTIONS,
  templatesForCollection,
  DEFAULT_COLLECTION_ID,
} from '@captionflow/template-schema';
import {
  captionStateAt,
  layoutSegment,
  drawCaption,
  type CaptionStyle,
  type CaptionPlacement,
  type CaptionSegment,
  type TranscriptWord,
  type Ctx2D,
} from '@captionflow/caption-engine';
import { browserMeasurer } from '../engine/measurer';
import { useEditor } from '../state/editorStore';

// A tiny fixed sample used for every thumbnail: three normal words + one
// emphasized/active word, so each card shows how the style pops a key word.
const SAMPLE_WORDS: TranscriptWord[] = [
  { id: 'p0', text: 'Go', startMs: 0, endMs: 400 },
  { id: 'p1', text: 'big', startMs: 400, endMs: 1200, emphasized: true },
];
const SAMPLE_SEGMENT: CaptionSegment = { id: 'seg', wordIds: ['p0', 'p1'], startMs: 0, endMs: 1200 };
const SAMPLE_WORD_MAP = new Map(SAMPLE_WORDS.map((w) => [w.id, w]));
// A moment where the emphasized word is the active one → active-word / karaoke
// templates highlight it, and the entrance animation has settled.
const SAMPLE_TIME_MS = 900;
// The caption's target height as a fraction of the thumbnail, so text fills the
// card regardless of a template's own reference font size.
const CAP_FRACTION = 0.4;

/**
 * Accurate style thumbnail: draws the SAME caption the export/preview would, via
 * the shared caption engine (`layoutSegment` + `drawCaption`), onto a small
 * canvas. Because the engine scales fonts by frame.height/REFERENCE_HEIGHT, we
 * pick a virtual frame height per template so the sample fills the card, then
 * center it. This makes each card a true miniature of the caption, not an
 * approximation.
 */
function TemplateThumb({ style, background }: { style: CaptionStyle; background: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) return;
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const cw = Math.round(rect.width * dpr);
      const ch = Math.round(rect.height * dpr);
      if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw;
        canvas.height = ch;
      }
      ctx.clearRect(0, 0, cw, ch);
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, cw, ch);

      const state = captionStateAt([SAMPLE_SEGMENT], SAMPLE_WORD_MAP, SAMPLE_TIME_MS);
      if (!state.segment || state.words.length === 0) return;
      // Choose a frame height so the rendered font ≈ CAP_FRACTION of the card.
      const targetCapPx = ch * CAP_FRACTION;
      const frameH = (targetCapPx / style.fontSizePx) * 1920;
      const placement: CaptionPlacement = { anchor: 'center', xNorm: 0.5, yNorm: 0.5, maxWidthNorm: 0.94 };
      const layout = layoutSegment(state.words, style, placement, { width: cw, height: frameH }, browserMeasurer);
      ctx.save();
      ctx.translate(0, (ch - frameH) / 2); // map the frame's vertical center to the card's
      drawCaption(ctx as unknown as Ctx2D, { style, layout, state, measurer: browserMeasurer });
      ctx.restore();
    };

    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [style, background]);

  return <canvas ref={ref} className="swatch-canvas" />;
}

/** The card preview: a saved 400×400 skin image (in /public/caption-skins),
 *  falling back to a live engine render for any template without one. */
function SkinPreview({ id, style, background }: { id: string; style: CaptionStyle; background: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <TemplateThumb style={style} background={background} />;
  return (
    <img
      className="swatch-canvas"
      src={`/caption-skins/${id}.webp`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

/** Browse + select templates by collection. Selecting a template updates the
 *  preview locally and instantly - no server round-trip. */
export function TemplatePicker() {
  const templateId = useEditor((s) => s.templateId);
  const setTemplate = useEditor((s) => s.setTemplate);
  const segments = useEditor((s) => s.segments);
  const selectedSegmentIds = useEditor((s) => s.selectedSegmentIds);
  const setSegmentTemplate = useEditor((s) => s.setSegmentTemplate);
  const clearSegmentSelection = useEditor((s) => s.clearSegmentSelection);
  const [collection, setCollection] = useState(DEFAULT_COLLECTION_ID);

  // When one or more caption lines are selected, the picker applies a per-line
  // style to just those lines; otherwise it sets the whole-video default.
  const targetingLines = selectedSegmentIds.length > 0;
  const selectedTemplateIds = new Set(
    segments.filter((s) => selectedSegmentIds.includes(s.id)).map((s) => s.templateId ?? templateId),
  );
  // The template to show as "current": the shared one across selected lines
  // (or the global default when not targeting lines / mixed selection).
  const activeId = targetingLines
    ? selectedTemplateIds.size === 1
      ? [...selectedTemplateIds][0]
      : null
    : templateId;

  const applyTemplate = (id: string) => {
    if (targetingLines) setSegmentTemplate(selectedSegmentIds, id);
    else setTemplate(id);
  };

  const list = useMemo(
    () =>
      templatesForCollection(collection)
        .map((id) => TEMPLATES_BY_ID[id])
        .filter((t): t is NonNullable<typeof t> => Boolean(t)),
    [collection],
  );

  return (
    <div className="template-picker">
      {targetingLines && (
        <div className="picker-scope">
          <span>
            Styling <strong>{selectedSegmentIds.length}</strong> selected line
            {selectedSegmentIds.length > 1 ? 's' : ''}
          </span>
          <div className="picker-scope-actions">
            <button onClick={() => setSegmentTemplate(selectedSegmentIds, null)}>
              Reset to default
            </button>
            <button onClick={clearSegmentSelection}>Done</button>
          </div>
        </div>
      )}
      <div className="cat-row">
        {TEMPLATE_COLLECTIONS.map((c) => (
          <button
            key={c.id}
            className={c.id === collection ? 'chip on' : 'chip'}
            onClick={() => setCollection(c.id)}
          >
            {c.label}
            {c.ai && <span className="ai-badge">AI</span>}
          </button>
        ))}
      </div>
      <div className="template-grid">
        {list.map((t) => {
          const emph = t.style.emphasis;
          const strongEmphasis = emph.scale >= 1.2;
          // The card ground: the template's own caption background when it has a
          // solid one, else a neutral dark so light captions read clearly.
          const ground =
            t.style.background.mode !== 'none' && t.style.background.color ? t.style.background.color : '#0d0f14';
          return (
            <button
              key={t.id}
              className={t.id === activeId ? 'template-card selected' : 'template-card'}
              onClick={() => applyTemplate(t.id)}
              title={t.description}
            >
              <div className="swatch swatch-skin" style={{ background: ground }}>
                {/* A saved preview image of the style; falls back to a live
                    engine render if the skin image is missing (new templates). */}
                <SkinPreview id={t.id} style={t.style} background={ground} />
                {strongEmphasis && (
                  <span className="emph-badge" title="Pops the key word big">
                    A<sup>+</sup>
                  </span>
                )}
              </div>
              <div className="template-meta">
                <strong>
                  {t.name}
                  {t.ai && <span className="ai-badge">AI</span>}
                </strong>
                <small>{t.renderMode === 'behind' ? 'depth · behind subject' : t.category}</small>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
